import {
  unitQuantityOfPieces,
  type BundleProductLengthLike,
} from '~/components/ui/bundleQuantity';
import type { UnitCode } from '~/graphql/graphql';
import { sellerLabel } from '~/utils';
import { hasOneCurrency, lowestPrice, type Price } from '~/utils/money';
import {
  isAwaitingSellerResponse,
  type NegotiableQuote,
} from './quoteNegotiation';

type OrderableQuote = NegotiableQuote & {
  sellingOrganization: {
    name: string;
    sellingOrganizationCode?: string | null;
  };
};

type OptionalPrice = Price | null | undefined;
type ItemWithDeliveryPrice = {
  offeredDeliveryPrice?: OptionalPrice;
};
type ItemWithOfferedQuantity = ItemWithDeliveryPrice & {
  quantityInPieces: number;
  platformProductsLength: BundleProductLengthLike & {
    platformProduct: { standardUnitsCode: UnitCode };
  };
};

export type RequestedLine = {
  platformProductLengthId: string;
  requestedQuantityInPieces: number;
};

export type QuoteOffering<TQuote, TItem> = {
  quote: TQuote;
  item: TItem;
};

function compareAmountsMissingLast(
  left: number | undefined,
  right: number | undefined
): number {
  if (left == null && right == null) {
    return 0;
  }
  if (left == null) {
    return 1;
  }
  if (right == null) {
    return -1;
  }
  return left - right;
}

function byDeliveryPriceThenSellerLabel<TEntry>(
  amountOf: (entry: TEntry) => number | undefined,
  quoteOf: (entry: TEntry) => OrderableQuote
): (left: TEntry, right: TEntry) => number {
  return (left, right) =>
    compareAmountsMissingLast(amountOf(left), amountOf(right)) ||
    sellerLabel(quoteOf(left).sellingOrganization).localeCompare(
      sellerLabel(quoteOf(right).sellingOrganization)
    );
}

function sortableAmount(
  price: OptionalPrice,
  isComparable: boolean
): number | undefined {
  if (price == null) {
    return undefined;
  }
  return isComparable ? price.amount : 0;
}

export function offeringQuotesByDeliveryPrice<
  TQuote extends OrderableQuote,
  TItem extends ItemWithDeliveryPrice,
>(
  quotes: readonly TQuote[],
  itemsByQuoteId: ReadonlyMap<string, ReadonlyMap<string, TItem>>,
  platformProductLengthId: string
): ReadonlyArray<QuoteOffering<TQuote, TItem>> {
  const offerings: QuoteOffering<TQuote, TItem>[] = [];
  for (const quote of quotes) {
    if (isAwaitingSellerResponse(quote)) {
      continue;
    }
    const item = itemsByQuoteId.get(quote.id)?.get(platformProductLengthId);
    if (item) {
      offerings.push({ quote, item });
    }
  }

  const isComparable = hasOneCurrency(
    offerings.map(({ item }) => item.offeredDeliveryPrice)
  );
  return offerings.sort(
    byDeliveryPriceThenSellerLabel(
      ({ item }) => sortableAmount(item.offeredDeliveryPrice, isComparable),
      ({ quote }) => quote
    )
  );
}

export function lowestPriceAmount<TItem>(
  offerings: ReadonlyArray<QuoteOffering<OrderableQuote, TItem>>,
  priceOf: (item: TItem) => OptionalPrice
): number | null {
  const lowest = lowestPrice(offerings.map(({ item }) => priceOf(item)));
  return lowest == null ? null : lowest.amount;
}

function quantityInPriceUnit(
  item: ItemWithOfferedQuantity,
  quantityInPieces: number
): number | null {
  const ppl = item.platformProductsLength;
  return unitQuantityOfPieces(
    ppl,
    quantityInPieces,
    ppl.platformProduct.standardUnitsCode
  );
}

type PricedLine = { price: Price; quantity: number | null };

type QuoteLines = { lines: PricedLine[]; isComplete: boolean };

function pricedRequestedLines(
  quote: OrderableQuote,
  itemsByLengthId: ReadonlyMap<string, ItemWithOfferedQuantity> | undefined,
  requestedLines: readonly RequestedLine[]
): QuoteLines {
  if (isAwaitingSellerResponse(quote) || itemsByLengthId == null) {
    return { lines: [], isComplete: false };
  }
  const lines: PricedLine[] = [];
  let isComplete = !offersUnrequestedLength(itemsByLengthId, requestedLines);
  for (const {
    platformProductLengthId,
    requestedQuantityInPieces,
  } of requestedLines) {
    const item = itemsByLengthId.get(platformProductLengthId);
    const price = item?.offeredDeliveryPrice;
    if (item == null || price == null) {
      isComplete = false;
      continue;
    }
    if (item.quantityInPieces < requestedQuantityInPieces) {
      isComplete = false;
    }
    lines.push({
      price,
      quantity: quantityInPriceUnit(item, requestedQuantityInPieces),
    });
  }
  return { lines, isComplete };
}

function offersUnrequestedLength(
  itemsByLengthId: ReadonlyMap<string, ItemWithOfferedQuantity>,
  requestedLines: readonly RequestedLine[]
): boolean {
  const requestedLengthIds = new Set(
    requestedLines.map(({ platformProductLengthId }) => platformProductLengthId)
  );
  for (const [lengthId, item] of itemsByLengthId) {
    if (!requestedLengthIds.has(lengthId) && item.quantityInPieces > 0) {
      return true;
    }
  }
  return false;
}

function sortableTotal(
  lines: readonly PricedLine[],
  isComparable: boolean
): number | undefined {
  if (lines.length === 0) {
    return undefined;
  }
  if (!isComparable) {
    return 0;
  }
  let total = 0;
  for (const { price, quantity } of lines) {
    if (quantity != null) {
      total += price.amount * quantity;
    }
  }
  return total;
}

export function quotesByTotalDeliveryPrice<
  TQuote extends OrderableQuote,
  TItem extends ItemWithOfferedQuantity,
>(
  quotes: readonly TQuote[],
  itemsByQuoteId: ReadonlyMap<string, ReadonlyMap<string, TItem>>,
  requestedLines: readonly RequestedLine[]
): ReadonlyArray<TQuote> {
  const linesByQuote = quotes.map((quote) => ({
    quote,
    ...pricedRequestedLines(
      quote,
      itemsByQuoteId.get(quote.id),
      requestedLines
    ),
  }));
  const isComparable = hasOneCurrency(
    linesByQuote.flatMap(({ lines }) => lines.map(({ price }) => price))
  );
  const ranked = linesByQuote.map(({ quote, lines, isComplete }) => ({
    quote,
    isComplete,
    amount: sortableTotal(lines, isComparable),
  }));

  const byPrice = byDeliveryPriceThenSellerLabel<(typeof ranked)[number]>(
    ({ amount }) => amount,
    ({ quote }) => quote
  );
  return ranked
    .sort(
      (left, right) =>
        Number(right.isComplete) - Number(left.isComplete) ||
        byPrice(left, right)
    )
    .map(({ quote }) => quote);
}

export function quotesByTallyDeliveryPrice<
  TQuote extends OrderableQuote & {
    offeredDeliveryPriceForTally?: OptionalPrice;
  },
>(quotes: readonly TQuote[]): ReadonlyArray<TQuote> {
  const tallyPriceOf = (quote: TQuote): OptionalPrice =>
    isAwaitingSellerResponse(quote)
      ? undefined
      : quote.offeredDeliveryPriceForTally;
  const isComparable = hasOneCurrency(quotes.map(tallyPriceOf));

  return [...quotes].sort(
    byDeliveryPriceThenSellerLabel(
      (quote) => sortableAmount(tallyPriceOf(quote), isComparable),
      (quote) => quote
    )
  );
}
